import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { appRoute } from "./route";
import { PageContainer, PageHeader } from "@fleet/ui";
import {
  REPORT_RANGES,
  type ReportRange,
  ReportsDashboard,
} from "@/features/reports/components/reports-dashboard";
import { RestaurantGate } from "@/features/pos/components/restaurant-gate";
import { useActiveContext } from "@/features/pos/hooks/use-active-context";
import { useI18n } from "@/providers/i18n-provider";
import { AppErrorBoundary } from "@/components/ui/error-boundary";

/**
 * Reports search params — make date range SHAREABLE via URL
 * (campaign #15b; born addressable as requested by audit):
 *   - `range`: `all` | `today` | `7d` | `30d`. Copy/pasting URL reproduces
 *     same time window as dashboard. Invalid value → `all`.
 */
interface ReportsSearch {
  range?: ReportRange;
}

function ReportsPage() {
  const { t } = useI18n();
  const ctx = useActiveContext();
  const search = useSearch({ from: reportsRoute.id });
  const navigate = useNavigate();
  return (
    <PageContainer maxWidth="5xl">
      <PageHeader title={t(msg`Reports`)} />
      <AppErrorBoundary region="reports" fill>
        <RestaurantGate ctx={ctx}>
          {(c) => (
            <ReportsDashboard
              org={c.org}
              branch={c.branch}
              rangeParam={() => search().range}
              onRangeChange={(range) => {
                void navigate({
                  to: "/reports",
                  search: (prev: ReportsSearch) => ({
                    ...prev,
                    range: range === "all" ? undefined : range,
                  }),
                  replace: true,
                });
              }}
            />
          )}
        </RestaurantGate>
      </AppErrorBoundary>
    </PageContainer>
  );
}

export const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  validateSearch: (search: Record<string, unknown>): ReportsSearch => ({
    range: REPORT_RANGES.find((r) => r === search.range),
  }),
  component: ReportsPage,
});
